import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useToast } from '../../../context/ToastContext';
import { supabase } from '../../../utils/supabase';
import { Achievement } from '../../../hooks/useAchievements';
import { 
  IoSparklesOutline,
  IoCheckmarkCircleOutline,
  IoAddOutline,
  IoCloseOutline,
  IoRefreshOutline,
  IoTrendingUpOutline
} from 'react-icons/io5';

interface TalkingPointsGeneratorProps {
  focusId: string;
  selectedAchievements: Achievement[];
  onTalkingPointsGenerated: (talkingPoints: any, metrics: any[]) => void;
  existingTalkingPoints?: any;
}

interface TalkingPoints {
  accomplishments: string[];
  challenges: string[];
  growth: string[];
  futureGoals: string[];
}

const TalkingPointsGenerator: React.FC<TalkingPointsGeneratorProps> = ({
  focusId,
  selectedAchievements,
  onTalkingPointsGenerated,
  existingTalkingPoints
}) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const [talkingPoints, setTalkingPoints] = useState<TalkingPoints>(
    existingTalkingPoints || {
      accomplishments: [],
      challenges: [],
      growth: [],
      futureGoals: []
    }
  );
  const [keyMetrics, setKeyMetrics] = useState<any[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [editingPoint, setEditingPoint] = useState<{ section: keyof TalkingPoints; index: number } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Style classes
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-700' : 'bg-white';

  // Fetch key metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const { data, error } = await supabase
          .from('review_metrics')
          .select('*')
          .eq('focus_id', focusId)
          .order('metric_date', { ascending: false });

        if (error) throw error;

        // Get unique metrics with their latest values
        const latestMetrics = data?.reduce((acc: any[], metric) => {
          const existing = acc.find(m => m.metric_name === metric.metric_name);
          if (!existing) {
            acc.push(metric);
          }
          return acc;
        }, []) || [];

        // Calculate improvements
        const metricsWithProgress = latestMetrics.map(metric => {
          const improvement = metric.baseline_value
            ? ((metric.metric_value - metric.baseline_value) / metric.baseline_value * 100).toFixed(1)
            : null;
          return { ...metric, improvement };
        });

        setKeyMetrics(metricsWithProgress.slice(0, 5)); // Top 5 metrics
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoadingMetrics(false);
      }
    };

    fetchMetrics();
  }, [focusId]);

  // Generate talking points based on achievements and metrics
  const generateTalkingPoints = () => {
    setIsGenerating(true);

    // Simulate AI generation with rule-based system
    setTimeout(() => {
      const newTalkingPoints: TalkingPoints = {
        accomplishments: [],
        challenges: [],
        growth: [],
        futureGoals: []
      };

      // Generate accomplishment talking points from achievements
      selectedAchievements.forEach(achievement => {
        if (achievement.impact_score && achievement.impact_score >= 4) {
          newTalkingPoints.accomplishments.push(
            `Delivered ${achievement.title} resulting in ${achievement.result}`
          );
        }
      });

      // Add metric-based accomplishments
      keyMetrics.forEach(metric => {
        if (metric.improvement && parseFloat(metric.improvement) > 0) {
          newTalkingPoints.accomplishments.push(
            `Improved ${metric.metric_name} by ${metric.improvement}% from ${metric.baseline_value} to ${metric.metric_value} ${metric.metric_unit || ''}`
          );
        }
      });

      // Generate challenges overcome
      selectedAchievements.forEach(achievement => {
        if (achievement.situation && achievement.task) {
          newTalkingPoints.challenges.push(
            `Overcame ${achievement.situation.split('.')[0].toLowerCase()} by ${achievement.action.split('.')[0].toLowerCase()}`
          );
        }
      });

      // Generate growth points
      const uniqueSkills = new Set<string>();
      selectedAchievements.forEach(achievement => {
        achievement.key_skills?.forEach(skill => uniqueSkills.add(skill));
      });
      
      if (uniqueSkills.size > 0) {
        newTalkingPoints.growth.push(
          `Developed expertise in ${Array.from(uniqueSkills).slice(0, 3).join(', ')}`
        );
      }

      // Generate future goals based on categories
      const categories = new Set(selectedAchievements.map(a => a.category).filter(Boolean));
      if (categories.has('leadership')) {
        newTalkingPoints.futureGoals.push('Take on more leadership responsibilities and mentor junior team members');
      }
      if (categories.has('technical')) {
        newTalkingPoints.futureGoals.push('Lead technical initiatives and drive architectural improvements');
      }
      if (categories.has('innovation')) {
        newTalkingPoints.futureGoals.push('Spearhead innovative projects that drive business value');
      }

      setTalkingPoints(newTalkingPoints);
      onTalkingPointsGenerated(newTalkingPoints, keyMetrics);
      setIsGenerating(false);
      showToast('Talking points generated successfully!', 'success');
    }, 2000);
  };

  const addCustomPoint = (section: keyof TalkingPoints) => {
    const newPoint = prompt(`Add a ${section.slice(0, -1)}:`);
    if (newPoint) {
      const updated = {
        ...talkingPoints,
        [section]: [...talkingPoints[section], newPoint]
      };
      setTalkingPoints(updated);
      onTalkingPointsGenerated(updated, keyMetrics);
    }
  };

  const removePoint = (section: keyof TalkingPoints, index: number) => {
    const updated = {
      ...talkingPoints,
      [section]: talkingPoints[section].filter((_, i) => i !== index)
    };
    setTalkingPoints(updated);
    onTalkingPointsGenerated(updated, keyMetrics);
  };

  const startEdit = (section: keyof TalkingPoints, index: number) => {
    setEditingPoint({ section, index });
    setEditValue(talkingPoints[section][index]);
  };

  const saveEdit = () => {
    if (editingPoint) {
      const updated = {
        ...talkingPoints,
        [editingPoint.section]: talkingPoints[editingPoint.section].map((point, i) =>
          i === editingPoint.index ? editValue : point
        )
      };
      setTalkingPoints(updated);
      onTalkingPointsGenerated(updated, keyMetrics);
      setEditingPoint(null);
      setEditValue('');
    }
  };

  const sections: { key: keyof TalkingPoints; title: string; icon: any; description: string }[] = [
    { 
      key: 'accomplishments', 
      title: 'Key Accomplishments', 
      icon: '🎯',
      description: 'Highlight your biggest wins and impact'
    },
    { 
      key: 'challenges', 
      title: 'Challenges Overcome', 
      icon: '💪',
      description: 'Show resilience and problem-solving skills'
    },
    { 
      key: 'growth', 
      title: 'Growth & Development', 
      icon: '📈',
      description: 'Demonstrate continuous learning'
    },
    { 
      key: 'futureGoals', 
      title: 'Future Goals', 
      icon: '🚀',
      description: 'Share your vision and ambitions'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Metrics Summary */}
      {!loadingMetrics && keyMetrics.length > 0 && (
        <div className={`p-6 rounded-lg ${cardBg} border ${borderColor}`}>
          <h3 className={`text-lg font-medium ${textColor} mb-4 flex items-center gap-2`}>
            <IoTrendingUpOutline className="w-5 h-5" />
            Key Metrics Performance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {keyMetrics.map((metric, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
              >
                <div className={`text-sm ${mutedTextColor} mb-1`}>
                  {metric.metric_name}
                </div>
                <div className={`text-xl font-bold ${textColor}`}>
                  {metric.metric_value} {metric.metric_unit}
                </div>
                {metric.improvement && (
                  <div className={`text-sm ${
                    parseFloat(metric.improvement) > 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {parseFloat(metric.improvement) > 0 ? '+' : ''}{metric.improvement}% from baseline
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generate Button */}
      {Object.values(talkingPoints).every(arr => arr.length === 0) && (
        <div className="text-center">
          <button
            onClick={generateTalkingPoints}
            disabled={isGenerating || selectedAchievements.length === 0}
            className={`inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors ${
              isGenerating || selectedAchievements.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                <span>Generating talking points...</span>
              </>
            ) : (
              <>
                <IoSparklesOutline className="w-5 h-5" />
                <span>Generate Talking Points</span>
              </>
            )}
          </button>
          {selectedAchievements.length === 0 && (
            <p className={`text-sm ${mutedTextColor} mt-2`}>
              Please select achievements first
            </p>
          )}
        </div>
      )}

      {/* Talking Points Sections */}
      {Object.values(talkingPoints).some(arr => arr.length > 0) && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={generateTalkingPoints}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${borderColor} ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              <IoRefreshOutline className="w-4 h-4" />
              <span>Regenerate</span>
            </button>
          </div>

          {sections.map(section => (
            <div key={section.key} className={`p-6 rounded-lg ${cardBg} border ${borderColor}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`text-lg font-medium ${textColor} flex items-center gap-2`}>
                    <span className="text-2xl">{section.icon}</span>
                    {section.title}
                  </h3>
                  <p className={`text-sm ${mutedTextColor} mt-1`}>
                    {section.description}
                  </p>
                </div>
                <button
                  onClick={() => addCustomPoint(section.key)}
                  className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                  title="Add custom point"
                >
                  <IoAddOutline className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                {talkingPoints[section.key].length === 0 ? (
                  <p className={`text-sm ${mutedTextColor} italic`}>
                    No talking points yet. Click the + button to add one.
                  </p>
                ) : (
                  talkingPoints[section.key].map((point, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        darkMode ? 'bg-gray-700' : 'bg-gray-50'
                      }`}
                    >
                      <IoCheckmarkCircleOutline className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      
                      {editingPoint?.section === section.key && editingPoint?.index === index ? (
                        <div className="flex-1">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                            className={`w-full px-3 py-1 rounded border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                            autoFocus
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={saveEdit}
                              className="text-sm text-green-600 hover:text-green-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingPoint(null);
                                setEditValue('');
                              }}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className={`flex-1 ${textColor} cursor-pointer hover:text-purple-600`}
                             onClick={() => startEdit(section.key, index)}>
                            {point}
                          </p>
                          <button
                            onClick={() => removePoint(section.key, index)}
                            className={`p-1 rounded hover:bg-gray-600 transition-colors`}
                            title="Remove"
                          >
                            <IoCloseOutline className="w-4 h-4 text-red-500" />
                          </button>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      <div className={`p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800`}>
        <h4 className={`font-medium text-blue-800 dark:text-blue-200 mb-2`}>
          Tips for Great Talking Points
        </h4>
        <ul className={`text-sm text-blue-700 dark:text-blue-300 space-y-1`}>
          <li>• Be specific and use quantifiable results when possible</li>
          <li>• Frame challenges as opportunities for growth</li>
          <li>• Connect your accomplishments to business impact</li>
          <li>• Keep talking points concise and memorable</li>
        </ul>
      </div>
    </div>
  );
};

export default TalkingPointsGenerator;